import { computed, inject } from "@angular/core";
import {
    getState,
    patchState,
    signalStore,
    withComputed,
    withMethods,
    withState,
} from "@ngrx/signals";

import { firstValueFrom } from "rxjs";

import { RemoteAppointmentTypeEnum } from "@hermes/api-model-remote-appointment";

import { Appointment } from "../models/remote-rendez-vous-appointment.model";
import { AppointmentsService } from "../services/appointments.service";

import { initialAppointmentsState } from "./appointments.state";

// eslint-disable-next-line @typescript-eslint/naming-convention
export const AppointmentsSignalStore = signalStore(
    withState(initialAppointmentsState),

    withComputed((store) => ({
        expertCallAppointments: computed(() =>
            store
                .appointments()
                ?.filter(
                    (appointment) =>
                        appointment.type ===
                        RemoteAppointmentTypeEnum.ExpertCall,
                ),
        ),
        scheduledExpertCallAppointments: computed(() => {
            const now = new Date();
            const todayMidnight = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate(),
            );

            return store.appointments()?.filter((appointment) => {
                const startDate = new Date(appointment.startDate);
                const appointmentDate = new Date(
                    startDate.getFullYear(),
                    startDate.getMonth(),
                    startDate.getDate(),
                );

                return (
                    appointment.type === RemoteAppointmentTypeEnum.ExpertCall &&
                    !appointment.cancelled &&
                    appointmentDate >= todayMidnight
                );
            });
        }),
        remoteRendezVousHasAppointments: computed(() =>
            store
                .appointments()
                .some(
                    (appointment) =>
                        appointment.type ===
                        RemoteAppointmentTypeEnum.RemoteRendezVous,
                ),
        ),
        remoteRendezVousAppointments: computed(() =>
            store
                .appointments()
                ?.filter(
                    (appointment) =>
                        appointment.type ===
                        RemoteAppointmentTypeEnum.RemoteRendezVous,
                ),
        ),
        scheduledRemoteRendezVousAppointments: computed(() => {
            const now = new Date();
            const todayMidnight = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate(),
            );

            return store.appointments()?.filter((appointment) => {
                const startDate = new Date(appointment.startDate);
                const appointmentDate = new Date(
                    startDate.getFullYear(),
                    startDate.getMonth(),
                    startDate.getDate(),
                );

                return (
                    appointment.type ===
                        RemoteAppointmentTypeEnum.RemoteRendezVous &&
                    !appointment.cancelled &&
                    appointmentDate >= todayMidnight
                );
            });
        }),
    })),

    withMethods((store) => {
        const appointmentsService = inject(AppointmentsService);

        return {
            load() {
                patchState(store, { isLoading: true });
            },
            cancelLoading() {
                patchState(store, { isLoading: false });
            },
            async fetchAppointments() {
                patchState(store, { isLoading: true });

                const appointments = await firstValueFrom(
                    appointmentsService.getAppointments(),
                );

                patchState(store, { isLoading: false, appointments });
            },
            cancel(appointmentId: string) {
                const appointments = store
                    .appointments()
                    ?.map((appointment) => {
                        if (appointment.id === appointmentId) {
                            return { ...appointment, cancelled: true };
                        }
                        return appointment;
                    });
                patchState(store, { appointments });
            },
            resetAppointments() {
                patchState(store, initialAppointmentsState);
            },
            setAppointments(appointments: Appointment[]) {
                patchState(store, {
                    appointments,
                });
            },
            setCurrentAppointmentId(appointmentId: string) {
                patchState(store, {
                    currentAppointmentId: appointmentId,
                });
            },
            fetchCurrentAppointmentId: computed(
                (): string | undefined => getState(store).currentAppointmentId,
            ),
        };
    }),
);
